<template>
  <div
    v-if="$ready_optimizer_input"
    :class="{
      'dayplanner-page-editor': true,
      'dayplan--column-layout': column_layout && !view_is_multi_day,
      'dayplan--multi-column-layout': view_is_multi_day,
      'dayplan--show-task': $route.name === 'dayplanner-taskpage',
      'dayplan--reduced': show_reduced_dayplan,
      'dayplan-sidebar--show': dayplanner_sidebar,
      'dayplan-sidebar--hide': !dayplanner_sidebar,
    }">
    <div class="dayplanner-page-suggestions">
      <DayplannerSidebar
        @add="onAddItem"
        @first-move="onUpdatePlaceholder( false, $event )"
        @drag-end="onUpdatePlaceholder( true, $event )"
      />
    </div>

    <div class="dayplanner-page-editor-main">
      <DayplannerNavigationBar />

      <!-- TODO: keep cached -->
      <template v-if="$route.name === 'monthplanner'">

        <MonthPlanner :view-date-range="view_date_range" />

      </template>
      <template v-else-if="$route.name === 'weekplanner'">

        <TableViewGoal :view-date-range="view_date_range" />

      </template>
      <template v-else>

        <div
          class="dayplan-outer-container d-flex"
          :style="custom_background"
          @dblclick.self="onDblClickDayplan"
          @mousedown.self="onDayplanMouseDown">

            <!--
            <div style="display: flex; flex-direction: row; flex-wrap: nowrap; justify-content:center; flex: 1">
              <div style="display: flex; flex-direction: row; flex-wrap: nowrap; justify-content: space-evenly">
              </div>
            </div>
            -->
            <DayplannerFlowchart
              v-for="day in view_date_range"
              :key="day.$key"
              ref="day_flowchart"
              :view-date="day.view_date"
              :memberId="$models.user.defaultUserId" />

          <!-- INFO: We also want to compare different peoples dayplans here -->
          <!-- INFO: We also might compare different 'versions' of a dayplan here (soll/ist) -->
        </div>
      </template>

      <template v-if="SHOW_DEBUG && false">
        <pre class="mini-pre">view_date_range: {{view_date_range}}</pre>
        <pre class="mini-pre">view_from_date: {{view_from_date}}</pre>
        <pre class="mini-pre">view_from_date_as_timestamp: {{view_from_date_as_timestamp}}</pre>
        <pre class="mini-pre">view_from_date_as_yymmdd: {{view_from_date_as_yymmdd}}</pre>
        <pre class="mini-pre">view_to_date: {{view_to_date}}</pre>
        <pre class="mini-pre">view_to_date_as_timestamp: {{view_to_date_as_timestamp}}</pre>
        <pre class="mini-pre">view_to_date_as_yymmdd: {{view_to_date_as_yymmdd}}</pre>
        <pre class="mini-pre">view_is_multi_day: {{view_is_multi_day}}</pre>
        <pre class="mini-pre">view_selected_date: {{view_selected_date}}</pre>
        <pre class="mini-pre">view_selected_date_as_timestamp: {{view_selected_date_as_timestamp}}</pre>
        <pre class="mini-pre">view_selected_date_as_yymmdd: {{view_selected_date_as_yymmdd}}</pre>

        <pre class="mini-pre">dayplanner_sidebar: {{dayplanner_sidebar}}</pre>
        <pre class="mini-pre">show_nothing_planned: {{show_nothing_planned}}</pre>
        <pre class="mini-pre">column_layout: {{column_layout}}</pre>
        <pre class="mini-pre">drag_item: {{drag_item}}</pre>
        <pre class="mini-pre">dayplan_max_sortidx: {{dayplan_max_sortidx}}</pre>
        <pre class="mini-pre">dayplan_remaining_timeslots: {{dayplan_remaining_timeslots}}</pre>
        <pre class="mini-pre">dayplanner_sessions: {{dayplanner_sessions}}</pre>
        <pre class="mini-pre">dayplan_from_db_at_selected_day: {{dayplan_from_db_at_selected_day}}</pre>
        <pre class="mini-pre">commitment_data_from_optimizer: {{commitment_data_from_optimizer}}</pre>
      </template>
    </div>

  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { mapReady } from "@/api/misc";

import DayplannerSidebar from "@/components/TodayPage/DayplannerSidebar";
import DayplannerNavigationBar from "@/components/TodayPage/DayplannerNavigationBar";
import DayplannerFlowchart from "@/components/TodayPage/DayplannerFlowchart"; // = 1 day
import MonthPlanner from "@/components/MonthPlannerPage/MonthPlanner"
import TableViewGoal from "@/components/MonthPlannerPage/TableViewGoal"
import dayplanner_watchers from '@/store/dayplanner/watchers'

const ENABLE_OPTIMIZER_SUGGESTIONS = true;

// TODO: Highlight today
// TODO: Disable column layout in week mode

const log0 = (...args) => console.log("%c" + args[0], "color: #69b42d; background-color: #e0e0e0", ...args.slice(1));

export default {
  mixins: [ dayplanner_watchers ],
  data: () => ({
    show_drag_placeholder: true,
  }),
  created() {
    log0("[DayplanEditor] CREATED");

    if (!this.commitmentData) {
      this.optimizer_set_manual_dayplan({
        date: this.view_selected_date_as_yymmdd,
        dayplan: []
      });
    }
  },
  watch: {
    commitment_data_from_optimizer( commitmentData )  {
      this.setOptimizerCommitmentCache( commitmentData );
    },
    dayplan_from_db_at_selected_day: {
      handler(dayplan_new) {
        log0("** WATCHER: dayplan **", dayplan_new);

        if ( ENABLE_OPTIMIZER_SUGGESTIONS ) {

          let selected_flowchart = this.getSelectedFlowchartComponent();

          if ( !selected_flowchart || !this.$ready_optimizer_input ) {
            console.warn("Flowchart or optimizer not ready")
            return
          }

          let dayplan_optimizer = selected_flowchart.getDayplanAsOptimizerInput();
          log0("Sending dayplan to optimizer", dayplan_optimizer, " for date:", this.view_selected_date_as_yymmdd);

          // TODO: Debounce
          this.optimizer_set_manual_dayplan({
            date: this.view_selected_date_as_yymmdd,
            dayplan: dayplan_optimizer
          });
        }

        // Call createSnapshot for undo function
        // this.createSnapshot( dayplan_new ) // This is AFTER not BEFORE the change (als 'old value' won't work btw.)
      },
      deep: true, // TODO: Deep watcher vs. hook in createSnapshot
    },
  },
  computed: {
    ...mapReady("goals", "commitments", "optimizer_input"),
    ...mapGetters("app", [
      "dayplanner_sidebar",
    ]),
    ...mapGetters("dayplanner", [
      "view_from_date",
      "view_from_date_as_timestamp",
      "view_from_date_as_yymmdd",
      "view_to_date",
      "view_to_date_as_timestamp",
      "view_to_date_as_yymmdd",
      "view_selected_date",
      "view_selected_date_as_timestamp",
      "view_selected_date_as_yymmdd",
      "view_is_multi_day",
      'show_reduced_dayplan',
      "show_nothing_planned",
      "column_layout",
      "drag_item",
      "drag_item",
      "dayplan_from_db_at_selected_day",
      "dayplan_max_sortidx",
      "dayplan_remaining_timeslots",
      "dayplanner_sessions",
    ]),
    ...mapGetters("optimizer", [
      "optimizer_manual_dayplan",
      "optimizer_finish_dates_commitments",
      "optimizer_result_data",
    ]),

    view_date_range() {
      if ( this.view_is_multi_day ) {
        let from = this.view_from_date;
        let to   = this.view_to_date;
        let cur  = from.clone();
        let days = [];

        do {
          days.push({
            $key:      'day-' + cur.format('YYMMDD'),
            view_date: cur.clone()
          });
        } while ( cur.add(1, 'days').diff( to ) <= 0 )

        return days;
      }

      return [{
        $key:      'day-' + this.view_from_date_as_yymmdd,
        view_date: this.view_from_date
      }];
    },

    commitment_data_from_optimizer() {
      return this.optimizer_result_data["commitmentData"];
    },

    custom_background() {
      let userSettings = this.$models.userSettings.getNode(this.$models.user.defaultUserId);
      let bgColorOrPicture = userSettings["dayplanner.bgPictureOrColor"];
      if (bgColorOrPicture) {
        if (bgColorOrPicture[0] === "#") {
          return { backgroundColor: bgColorOrPicture, backgroundImage: "unset" };
        }
        return { backgroundImage: "url(" + bgColorOrPicture + ")" };
      }
      return {};
    },
  },
  methods: {
    ...mapMutations("dayplanner", [
      'TOGGLE_SHOW_REDUCED_DAYPLAN',
    ]),
    ...mapActions("optimizer", [
      "optimizer_set_manual_dayplan",
    ]),
    ...mapActions("dayplanner", [
      'setOptimizerCommitmentCache',
    ]),

    getSelectedFlowchartComponent() {
      // TODO: sometimes it does not work - after HMR?
      return Array.isArray( this.$refs.day_flowchart )
        ? this.$refs.day_flowchart.find(item => {
            return item.viewDate.format('YYMMDD') === this.view_selected_date_as_yymmdd
          })
        : this.$refs.day_flowchart;
    },

    onAddItem(item, mode = "auto", idx = null) {

      console.log("#### onAddItem", item, mode, idx)

      /* delegate task to corrent lane */
      let selected_flowchart = this.getSelectedFlowchartComponent();
      if ( selected_flowchart ) {
        selected_flowchart.onAddItem(item, mode, idx);
      } else {
        this.$notify.warning({
          type: 'warning',
          message: 'No day selected',
          position: 'bottom-right'
        });
      }
    },

    onUpdatePlaceholder() {
      log0("onUpdatePlaceholder")
    },

    onDblClickDayplan() {
      this.TOGGLE_SHOW_REDUCED_DAYPLAN();
    },

    onDayplanMouseDown(event) {
      if ( event.detail > 1 ) {
        event.preventDefault();
      }
    },
  },
  components: {
    DayplannerSidebar,
    DayplannerNavigationBar,
    DayplannerFlowchart,
    MonthPlanner,
    TableViewGoal,
  },
};
</script>

<style>
.dayplanner-page-editor {
  display: flex;
  width: 100%;
}
.dayplanner-page-editor .dayplanner-page-editor-main {
  flex: auto;
  width: 100%;
}

.dayplanner-page-editor .dayplan-outer-container {
  margin: 0;
  /*padding: 4px;*/

  /*background-image: url('../../assets/backgrounds/what-the-hex-dark.png');*/
  /*background-image: url('../../assets/backgrounds/natural_paper.png');*/
  background-image: url("../../assets/backgrounds/leaves-pattern.png"); /* Default */
  background-size: cover;
  background-position-x: center;

  /*box-shadow: inset 0px 4px 6px 0px #444d563d;*/
  overflow-y: auto;
  overflow-x: auto;
  height: calc(100vh - 50px); /* = height top menu */
}
.dayplanner-page-editor.dayplan-sidebar--hide:not(.dayplan--show-task) .dayplan-outer-container {
  /*width: calc(100vw - 120px)*/
}
.dayplanner-page-editor.dayplan-sidebar--show:not(.dayplan--show-task) .dayplan-outer-container {
  /*width: calc(100vw - 495px); <<<<  TODO menubar */
}
.dayplanner-page-editor.dayplan-sidebar--hide.dayplan--show-task .dayplan-outer-container {
}
.dayplanner-page-editor.dayplan-sidebar--show.dayplan--show-task .dayplan-outer-container {
}
.dayplanner-page-editor .dayplanner-page-suggestions {
  width: 375px; /* = iPhone 8 screen width */
  flex: 0 0 375px;
  border-right: 1px solid #DCDFE6;
}
.dayplanner-page-editor .dayplanner-page-packs {
  border-left: 1px solid gray;
  overflow-y: scroll;
  height: 100vh;
}
.dayplanner-page-editor .dayplanner-topbar {
  height: 50px;
  border-bottom: 1px solid #DCDFE6;
}
.dayplanner-page-editor.dayplan-sidebar--show .dayplanner-page-suggestions {
  width: 375px; /* = iPhone 8 screen width */
  flex: 0 0 375px;
}
.dayplanner-page-editor.dayplan-sidebar--hide .dayplanner-page-suggestions {
  display: none;
}

.dayplanner-page-editor .list .list-inner .task {
  margin-left: 0;
  margin-right: 0;
}
.dayplanner-page-editor .list footer {
  padding: 0;
}

.dayplanner-page-editor .dayplanner-topbar .el-button {
  /* Remove button borders inside top bar */
  border: none;
}

/* ---------------------------------- print - -------------------------------- */
/* move to global print.css */
@media print {
  .sidebar-container,
  .topbar-container {
    display: none;
  }
  .el-button.seperator-button,
  .el-popover {
    display: none;
  }
  #app .hide-sidebar.hide-sessionbar .main-container {
    padding-left: 0;
    padding-top: 0;
  }
  .calendar-split-screen {
    height: auto;
  }
  .calendar-split-content {
    border: none;
  }

  .dayplanner-topbar .el-button,
  .dayplanner-topbar .el-button-group {
    display: none;
  }
  .dayplanner-page-editor .dayplanner-page-suggestions {
    display: none;
  }
  .dayplanner-page-editor .dayplan-outer-container {
    background: none;
    height: auto;
    overflow: scroll;
  }
  .dayplanner-page-editor .flow-placeholder,
  .dayplanner-page-editor .flow-day-marker {
    border: 2px solid black;
  }
  .dayplanner-page-editor .flow-block {
    border: none;
  }
  .dayplanner-page-editor .flow-block-header {
    border: 2px solid black;
    border-radius: 0;
  }
  .dayplanner-page-editor .flow-time-btn {
    display: none;
  }
  .dayplanner-page-editor .list footer {
    display: none !important;
  }
}

/* -------------------------------- month view -------------------------------- */

.open-menubar .dayplan-sidebar--hide .dayplan-table-outer-container {
  width: calc(100vw - 245px);
}
.hide-menubar .dayplan-sidebar--hide .dayplan-table-outer-container {
  width: calc(100vw);
}
.open-menubar .dayplan-sidebar--show .dayplan-table-outer-container {
  width: calc(100vw - 375px - 245px);
}
.hide-menubar .dayplan-sidebar--show .dayplan-table-outer-container {
  width: calc(100vw - 375px);
}
.dayplan-table-outer-container {
  display: block;
  overflow: scroll;
  height: calc(100vh - 50px);
}

/* -------------------------------- column layout -------------------------------- */

.dayplan--column-layout .dayplan-inner-container {
  height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-content: flex-start;
  margin-bottom: 1em;
  margin-right: 1em;
  margin-left: 1em;
  padding-right: 1em;
}

.dayplan--column-layout .dayplan-inner-container > * {
  margin-right: 1em;
}

/* --------------------------------- scroll bar --------------------------------- */
/* width */
.dayplan-table-outer-container::-webkit-scrollbar,
.dayplan-outer-container::-webkit-scrollbar {
  width: 15px;
  height: 15px;
  background: transparent;
}
/* Handle */
.dayplan-table-outer-container::-webkit-scrollbar-thumb,
.dayplan-outer-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.66);
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.66);
}
/* Handle on hover */
.dayplan-table-outer-container::-webkit-scrollbar-thumb:hover,
.dayplan-outer-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 1);
}

</style>
